import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import Separator from 'components/Separator';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useEffect } from 'react';
import { track_links } from 'utils/analytics/analytics';
import Plus18 from '../../../svg/FooterLogos/18+-footer.svg';
import FundraisingRegulator from '../../../svg/FooterLogos/fundraising-regulator-footer.svg';
import GamCare from '../../../svg/FooterLogos/gam-care-footer.svg';
import TrustPilot from '../../../svg/FooterLogos/trustpilot-in-footer.svg';
import DaymadeLogo from '../../../svg/Logo/Logo.svg';
import FooterWaveDesktop from '../../../svg/Waves/footer-wave-desktop.svg';
import FooterWaveMobile from '../../../svg/Waves/footer-wave-mobile.svg';
import FooterLinkColumn, { FooterHeaderItem } from './footer-links';
import SocialLinks from './footer-social-links';
import styles from './footer.module.css';
import NewsletterSignUp from './newsletter-sign-up';
import { PaymentLogos } from './payment-logos';

export type FooterBackgroundColors =
  | 'white'
  | 'absoluteWhite'
  | 'teal'
  | 'darkBlue';

const Footer2026 = observer(
  ({ background = 'darkBlue' }: { background?: FooterBackgroundColors }) => {
    const { currentUser, subscriptions } = useUserStore();

    const faqLink =
      currentUser?.id &&
      subscriptions.find(
        (s) =>
          s.subscription.status === 'ACTIVE' ||
          s.subscription.status === 'SNOOZED' ||
          s.subscription.status === 'FAILING',
      )
        ? 'https://daymade.zendesk.com/hc/en-gb'
        : '/faq';

    useEffect(() => {
      track_links('#facebookext', 'Clicked Facebook link');
      track_links('#instagramext', 'Clicked Instagram link');
      track_links('#youtubeext', 'Clicked Youtube link');
      track_links('#tiktokext', 'Clicked Tiktok link');
      track_links('#faqlink', 'Clicked FAQ link');
      track_links('#contactuslink', 'Clicked Contact us link');
    }, []);

    return (
      <footer
        className={clsx(
          'daymadeTypographyReset',
          styles.footer,
          styles['footerWaveBackground__' + background],
        )}
      >
        <div className={clsx(styles.footerWave, styles.footerWaveDesktop)}>
          <FooterWaveDesktop />
        </div>
        <div className={clsx(styles.footerWave, styles.footerWaveMobile)}>
          <FooterWaveMobile />
        </div>
        <div className={clsx(styles.footerContent)}>
          <AboutUsModule background="darkBlue" withReducedPadding>
            <AboutUsSiteContainer styles2026>
              <NewsletterSignUp />
            </AboutUsSiteContainer>
          </AboutUsModule>
          <Separator
            thickness="1px"
            colour="rgba(255,255,255,0.06)"
            margin={0}
          />
          <AboutUsModule background="darkBlue" withMorePadding>
            <AboutUsSiteContainer styles2026>
              <div className={styles.footerMain}>
                <div className={styles.spacedItems}>
                  <FadeIn onScroll>
                    <Link className={styles.footerLogo} href={'/'}>
                      <DaymadeLogo />
                    </Link>
                    <p
                      className={clsx(
                        'daymadeTypographySMedium',
                        styles.companyTagline,
                      )}
                    >
                      Win prizes that make your day
                    </p>
                  </FadeIn>
                  <FadeIn onScroll className={clsx(styles.firstFooterHeader)}>
                    <FooterHeaderItem
                      title="Follow us"
                      className={styles.followUs}
                    />
                    <div className={styles.socialLinks}>
                      <SocialLinks />
                    </div>
                  </FadeIn>
                </div>
                <FooterLinkColumn title="Company" col={1} faqLink={faqLink} />
                <FooterLinkColumn title="Legal" col={2} faqLink={faqLink} />
                <div className={styles.spacedItems}>
                  <div>
                    <FadeIn onScroll>
                      <FooterHeaderItem title="Payments we accept" />
                    </FadeIn>
                    <PaymentLogos />
                  </div>
                  <FadeIn onScroll className={styles.logos}>
                    <div className={styles.regulatorLogos}>
                      <Link
                        href="https://www.fundraisingregulator.org.uk/directory/daymade-ltd"
                        target="_blank"
                        rel="noreferrer"
                        className={styles.fundraising}
                      >
                        <FundraisingRegulator />
                      </Link>
                      <Link
                        href="https://www.gamcare.org.uk"
                        target="_blank"
                        rel="noreferrer"
                        className={styles.gamcare}
                      >
                        <GamCare />
                      </Link>
                      <div className={styles.plus18}>
                        <Plus18 />
                      </div>
                    </div>
                    <Link
                      target="_blank"
                      href="https://www.trustpilot.com/review/daymade.co.uk"
                      className={styles.trustpilot}
                    >
                      <TrustPilot />
                    </Link>
                  </FadeIn>
                </div>
              </div>
            </AboutUsSiteContainer>
          </AboutUsModule>
          <Separator
            thickness="1px"
            colour="rgba(255,255,255,0.06)"
            margin={0}
          />
          <AboutUsModule background="darkBlue" withReducedPadding>
            <AboutUsSiteContainer styles2026>
              <FadeIn onScroll className={styles.footerDisclaimer}>
                <div
                  className={clsx(
                    styles.disclaimerItem,
                    'daymadeTypographySMedium',
                  )}
                >
                  DAYMADE is an associated brand of, and operated by, DAYMADE
                  LTD (registered in England & Wales, company number 12337770).
                </div>
                <div className="daymadeTypographySRegular">
                  Please note that DAYMADE is an operator of prize draws. Our
                  online contests to win prizes include a method of entry via
                  post. No purchase is necessary to enter but the intention of
                  the draws is that people enter to win prizes and support a
                  worthy charitable cause.
                </div>
              </FadeIn>
            </AboutUsSiteContainer>
          </AboutUsModule>
        </div>
        {/* </div> */}
      </footer>
    );
  },
);

export default Footer2026;
